import {
  boostEligibility,
  boostStatus,
  BoostStatus,
  boostSubscribe,
  boostUnsubscribe,
  pollBoost,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE, logW } from "@blacknut/logging/dist";

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import React from "react";

import { PropsWithChildren } from "react";
import { forkJoin } from "rxjs";
import { useAppContext } from "./ContextProvider";
import { LOGGING_TAG } from "./logging";
import { RTCIceCandidatePair, usePlayerContext } from "./PlayerContext";
import { PlayerStatus } from "./PlayerUtils";

export const EricssonContext = createContext<{
  boostEnabled?: boolean;
  boostEligible?: boolean;

  toggleBoost: (
    candidates: RTCIceCandidate[],
    candidatePair?: RTCIceCandidatePair,
  ) => void;
}>({
  toggleBoost: () => {},
});

export const EricssonContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { config } = useAppContext();
  const [boostEnabled, setBoostEnabled] = useState<boolean>();
  const [boostEligible, setBoostEligible] = useState<boolean>();
  const pollBoostRef = useRef<ReturnType<typeof pollBoost>>();
  const { playerStatus } = usePlayerContext();
  const togglingBoost = useRef<number>();
  useEffect(() => {
    if (
      playerStatus === PlayerStatus.DISCONNECTED ||
      playerStatus === PlayerStatus.EXITING
    ) {
      logD(LOGGING_TAG, "Canceling boost polling");
      pollBoostRef.current?.cancel();
      pollBoostRef.current = undefined;
    }
  }, [playerStatus]);

  useEffect(() => {
    if (config?.features?.ericssonBoost === true) {
      forkJoin({ eligibility: boostEligibility(), status: boostStatus() }).subscribe({
        next: (data) => {
          setBoostEnabled(
            data.status.status === BoostStatus.ACCEPTED ||
              data.status.status === BoostStatus.ACTIVATED,
          );
          setBoostEligible(data.eligibility.isEligible);

          if (data.eligibility.isEligible) {
            pollBoostRef.current = pollBoost();
            pollBoostRef.current.onChange().subscribe({
              next: (status) => {
                logD(LOGGING_TAG, "Boot status has changed: %o", status);
                setBoostEnabled(
                  status === BoostStatus.ACCEPTED || status === BoostStatus.ACTIVATED,
                );
              },
            });
          }
        },
        error: (error) => {
          logE(LOGGING_TAG, "Caught error requesting status: %o", error);
          setBoostEligible(false);
          setBoostEnabled(false);
        },
      });
    }
  }, [config?.features?.ericssonBoost]);

  const toggleBoost = useCallback(
    (candidates: RTCIceCandidate[], candidatePair?: RTCIceCandidatePair) => {
      const current = new Date().getTime();
      if (togglingBoost.current && togglingBoost.current < current + 1000) {
        logW(
          LOGGING_TAG,
          "Ignore double boost: boost = %o, current = %o",
          togglingBoost.current,
          current,
        );
        return;
      }
      togglingBoost.current = new Date().getTime();
      const oldEnabled = boostEnabled;
      logD(
        LOGGING_TAG,
        "in toggle boost, enabled %o, candidates:%o, pair: %o/%o",
        oldEnabled,
        candidates,
        candidatePair?.local.toJSON(),
        candidatePair?.remote.toJSON(),
      );
      if (oldEnabled) {
        setBoostEnabled(false);
        boostUnsubscribe().subscribe({
          next: () => {},
          error: (error) => {
            setBoostEnabled(true);
            logE(LOGGING_TAG, "Caught error toggling boost status: %o", error);
          },
        });
      } else if (candidates && candidatePair) {
        setBoostEnabled(true);

        let sourceIp = "";
        let destinationCidr = "";
        let destinationPort = 0;
        let webrtcMode: "host" | "relay" = "host";
        let anyRelatedAddress: string | undefined = undefined;

        // lets find the source local ip using candidate with relay type
        // this will fix the plrfx selected candidate if any
        let relayCandidate: RTCIceCandidate | undefined = undefined;
        let hostCandidate: RTCIceCandidate | undefined = undefined;
        for (let i = 0; i < candidates.length; i++) {
          const candidate = candidates[i];
          if (
            !hostCandidate /*candidates are sorted by priority */ &&
            candidate.type === "host" &&
            candidate.address?.match(/^\d+\.\d+\.\d+\.\d+$/)
          ) {
            hostCandidate = candidate;
          } else if (
            !anyRelatedAddress &&
            candidate.relatedAddress?.match(/^\d+\.\d+\.\d+\.\d+$/)
          ) {
            anyRelatedAddress = candidate.relatedAddress;
          }
        }

        if (candidatePair.local.type === "relay") {
          sourceIp =
            hostCandidate?.address ||
            candidatePair.local.relatedAddress ||
            anyRelatedAddress ||
            "";
          destinationCidr = `${candidatePair.local.address || anyRelatedAddress}/32`;
          destinationPort = 5349;
        } else if (candidatePair.local.type === "host") {
          sourceIp =
            candidatePair.local.address ||
            hostCandidate?.address ||
            anyRelatedAddress ||
            "";
          destinationCidr = `${candidatePair.remote.address}/32`;
          destinationPort = candidatePair.remote.port || 0;
        } else {
          sourceIp =
            candidatePair.local.relatedAddress ||
            hostCandidate?.address ||
            anyRelatedAddress ||
            "";
          destinationCidr = `${candidatePair.remote.address}/32`;
          destinationPort = candidatePair.remote.port || 0;
        }
        if (candidatePair.local.type === "relay") {
          webrtcMode = "relay";
        }
        boostSubscribe({
          sourceIp,
          destinationCidr,
          destinationPort,
          webrtcMode,
        }).subscribe({
          next: () => {
            togglingBoost.current = new Date().getTime();
          },
          error: (error) => {
            togglingBoost.current = new Date().getTime();
            setBoostEnabled(false);
            logE("Caught error toggling boost status: %o", error);
          },
        });
      }
    },
    [boostEnabled],
  );
  return (
    <EricssonContext.Provider
      value={{
        boostEligible,
        boostEnabled,
        toggleBoost,
      }}
    >
      {children}
    </EricssonContext.Provider>
  );
};

export const useEricssonContext = () => useContext(EricssonContext);
